import { api } from "@/lib/api";
import { fmtMoney, fmtDate, vehicleLabel } from "@/lib/format";
import { buildReceiptBucket } from "@/lib/receiptBuckets";
import { Printer } from "@phosphor-icons/react";
import { resolveLogoUrl } from "@/components/LogoUploader";

function escHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

// Convert a number to Indian-style words (paise ignored). Used by the formal
// "Amount In Words" line on the Ex-showroom invoice.
function numToIndianWords(n) {
  n = Math.round(Number(n) || 0);
  if (n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigits = (x) => {
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    const t = Math.floor(x / 10);
    const o = x % 10;
    return tens[t] + (o ? " " + ones[o] : "");
  };
  const threeDigits = (x) => {
    const h = Math.floor(x / 100);
    const rest = x % 100;
    let s = "";
    if (h) s += ones[h] + " Hundred";
    if (rest) s += (h ? " " : "") + twoDigits(rest);
    return s;
  };
  const crore = Math.floor(n / 10000000);
  n %= 10000000;
  const lakh = Math.floor(n / 100000);
  n %= 100000;
  const thousand = Math.floor(n / 1000);
  n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(threeDigits(crore) + " Crore");
  if (lakh) parts.push(twoDigits(lakh) + " Lakh");
  if (thousand) parts.push(twoDigits(thousand) + " Thousand");
  if (hundred) parts.push(threeDigits(hundred));
  return parts.join(" ").replace(/\s+/g, " ").trim() + " Only";
}

const resolveLogo = resolveLogoUrl;

function invNo(numbers, kind, provisional) {
  return numbers[kind] || (provisional ? "DRAFT (not approved)" : "—");
}

// Auto-generated round company seal (SVG) for the seal & stamp area.
function sealSvg(settings) {
  const name = String(settings.name || "DEALERSHIP").toUpperCase();
  const addrParts = String(settings.address || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  const city = (addrParts[addrParts.length - 1] || settings.state_name || "").toUpperCase();
  const fs = name.length > 30 ? 9 : name.length > 22 ? 10 : 12;
  const fit = name.length > 14 ? ' textLength="168" lengthAdjust="spacingAndGlyphs"' : "";
  return `<svg class="seal" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" aria-label="Company seal">
    <defs>
      <path id="sealTop" d="M 20,80 A 60,60 0 0 1 140,80" />
      <path id="sealBot" d="M 24,80 A 56,56 0 0 0 136,80" />
    </defs>
    <g fill="none" stroke="#1b3a8a" stroke-width="2.2">
      <circle cx="80" cy="80" r="76"/><circle cx="80" cy="80" r="70" stroke-width="1"/><circle cx="80" cy="80" r="40" stroke-width="1.2"/>
    </g>
    <g fill="#1b3a8a" font-family="Arial, Helvetica, sans-serif" font-weight="700">
      <text font-size="${fs}"${fit}><textPath href="#sealTop" startOffset="50%" text-anchor="middle">${escHtml(name)}</textPath></text>
      <text font-size="9" letter-spacing="1.5" dy="7"><textPath href="#sealBot" startOffset="50%" text-anchor="middle">★ ${escHtml(city)} ★</textPath></text>
      <text x="80" y="77" font-size="9" text-anchor="middle">AUTHORISED</text>
      <text x="80" y="89" font-size="9" text-anchor="middle">SIGNATORY</text>
    </g>
  </svg>`;
}

function signaturesHtml(settings) {
  return `<div class="signatures">
      <div class="sig-col"></div>
      <div class="sig-col sig-with-seal">
        ${sealSvg(settings)}
        <div class="sig-line">&nbsp;</div>
        <div>For ${escHtml(settings.name || "Dealership")}<br/>Authorised Signatory</div>
      </div>
    </div>`;
}

// ----- Ex-showroom formal "VEHICLE INVOICE" panel -----
function exShowroomHtml(sale, settings, data, inv) {
  const v = sale.vehicle || {};
  const c = sale.customer || {};
  const vehLabel = vehicleLabel(v);
  // Derive vehicle HSN from the package master (preserved on the sale snapshot).
  // We pick the first ex_showroom-bucket item that carries an hsn_code.
  const vehicleHsn = (() => {
    for (const p of sale.packages || []) {
      for (const it of p.items_included || []) {
        if ((it.receipt_bucket || "other") === "ex_showroom" && it.hsn_code) return it.hsn_code;
      }
    }
    return "";
  })();
  // Total tax % from snapshot for the table row.
  const onRoadIgstPct = (() => {
    const p = (sale.packages || [])[0];
    return p?.receipt_gst?.ex_showroom?.igst_pct || 0;
  })();
  const onRoadCgstPct = (() => {
    const p = (sale.packages || [])[0];
    return p?.receipt_gst?.ex_showroom?.cgst_pct || 0;
  })();
  // `subtotal` and GST are calculated on the full pre-subsidy invoice value.
  // Subsidy/company discount stays internal and is not printed on the invoice.
  const taxable = data.subtotal;
  const igstAmt = data.igst_amt;
  const cgstAmt = data.cgst_amt;
  const fullInvoiceTotal = data.gross_total || data.total;
  const grandTotal = fullInvoiceTotal;
  const roundedTotal = Math.round(grandTotal);
  const roundOff = roundedTotal - grandTotal;

  const partRows = "";

  return `<section class="receipt formal" data-panel="ex_showroom">
    <button class="print-btn no-print" onclick="window.__printPanel('ex_showroom')">Print Ex-showroom</button>
    <header class="hdr">
      <div class="hdr-left">
        ${settings.logo_url ? `<img class="logo" src="${escHtml(resolveLogo(settings.logo_url))}" alt="logo" />` : `<div class="logo-fallback">${escHtml(settings.name || "DEALERSHIP").slice(0, 18)}</div>`}
      </div>
      <h2 class="hdr-title">VEHICLE INVOICE</h2>
    </header>
    <div class="meta">
      <div>Invoice No : <b>${escHtml(invNo(inv.numbers, "ex_showroom", inv.provisional))}</b></div>
      <div>Invoice Date: <b>${escHtml(fmtDate(sale.sale_date))}</b></div>
    </div>
    <hr/>
    <div class="party">
      <div class="party-col">
        <div class="party-name">${escHtml(settings.name || "—")}</div>
        <div>${escHtml((settings.address || "").split("\n").join(", "))}</div>
        <div>State Name: ${escHtml(settings.state_name || "—")} &nbsp; State Code: ${escHtml(settings.state_code || "—")}</div>
        <div>GST No : <b>${escHtml(settings.gstin || "—")}</b></div>
      </div>
      <div class="party-col">
        <div><span class="lbl">Customer</span> : <b>${escHtml(c.name || "—")}</b></div>
        <div><span class="lbl">Mobile</span> &nbsp;&nbsp;&nbsp;: ${escHtml(c.phone || "—")}</div>
        <div><span class="lbl">Address</span> : ${escHtml(c.address || "—")}</div>
        <div><span class="lbl">Cust GST No</span> : ${escHtml(c.gst_number || c.gstin || "—")}</div>
      </div>
    </div>

    <table class="charges">
      <thead><tr>
        <th>Particulars</th>
        <th>HSN</th>
        <th>Qty</th>
        <th>Unit Cost</th>
        <th>Disc</th>
        <th>Taxable</th>
        <th>CGST %</th>
        <th>Amount</th>
        <th>SGST %</th>
        <th>Amount</th>
        <th>Total</th>
      </tr></thead>
      <tbody>
        <tr class="lineitem">
          <td><b>${escHtml(vehLabel || "Vehicle")}</b></td>
          <td>${escHtml(vehicleHsn || "—")}</td>
          <td>1</td>
          <td class="num">${escHtml(fmtMoney(taxable))}</td>
          <td>0</td>
          <td class="num">${escHtml(fmtMoney(taxable))}</td>
          <td>${escHtml(onRoadCgstPct)}</td>
          <td class="num">${escHtml(fmtMoney(cgstAmt))}</td>
          <td>${escHtml(onRoadIgstPct)}</td>
          <td class="num">${escHtml(fmtMoney(igstAmt))}</td>
          <td class="num"><b>${escHtml(fmtMoney(fullInvoiceTotal))}</b></td>
        </tr>
        ${partRows ? `<tr><td colspan="11" class="subhdr">— Charge Breakdown —</td></tr>${partRows}` : ""}
      </tbody>
      <tfoot>
        <tr><td colspan="10" class="num"><b>Grand Total</b></td><td class="num"><b>${escHtml(fmtMoney(fullInvoiceTotal))}</b></td></tr>
        <tr><td colspan="10" class="num">Round Off</td><td class="num">${roundOff ? escHtml(fmtMoney(roundOff)) : "—"}</td></tr>
        <tr><td colspan="10" class="num"><b>Net Total</b></td><td class="num"><b>${escHtml(fmtMoney(roundedTotal))}</b></td></tr>
      </tfoot>
    </table>

    <div class="words">Amount In Words: <b>${escHtml(numToIndianWords(roundedTotal))}</b></div>

    <table class="parts">
      <thead><tr><th>Part Description</th><th>Frame No</th><th>Engine No</th><th>Booking No</th></tr></thead>
      <tbody><tr>
        <td>${escHtml(v.color || vehLabel || "—")}</td>
        <td class="mono">${escHtml(v.vin || v.chassis_no || "—")}</td>
        <td class="mono">${escHtml(v.engine_no || "—")}</td>
        <td class="mono">${escHtml(sale.booking_id ? sale.booking_id.slice(0, 8).toUpperCase() : "—")}</td>
      </tr></tbody>
    </table>

    <div class="hpa">H.P.A with : <b>${escHtml(sale.has_finance ? ((sale.finance || {}).financier_name || "Finance") : "Cash")}</b></div>

    <div class="exshow"><b>Ex-showroom Price of the Vehicle - Rs. ${escHtml(fmtMoney(roundedTotal))}</b></div>
    <div class="received">
      <div>Received :</div>
      <div>1. Tools</div>
      <div>2. Manual Book</div>
      <div>3. Duplicate Keys</div>
    </div>

    ${signaturesHtml(settings)}
  </section>`;
}

// ----- Helmet / Accessories Invoice (matches Ex-showroom format) -----
function simpleHtml(sale, bucket, title, data, settings, inv) {
  const c = sale.customer || {};
  // Helmet/Accessories prices are GST-INCLUSIVE. For each line we render full
  // GST-invoice columns derived from the per-line price and bucket %s.
  const igstPct = Number(data.igst_pct) || 0;
  const cgstPct = Number(data.cgst_pct) || 0;
  const combinedPct = igstPct + cgstPct;
  const deriveTaxable = (gross) =>
    combinedPct > 0 ? gross / (1 + combinedPct / 100) : gross;
  const itemRows = data.lines.map((l, idx) => {
    const gross = Number(l.price) || 0;
    const taxable = Math.round(deriveTaxable(gross) * 100) / 100;
    const cgstAmt = Math.round(((taxable * cgstPct) / 100) * 100) / 100;
    const igstAmt = Math.round(((taxable * igstPct) / 100) * 100) / 100;
    return `<tr class="lineitem">
      <td>${idx + 1}</td>
      <td><b>${escHtml(l.label)}</b></td>
      <td>${escHtml(l.hsn_code || "—")}</td>
      <td>1</td>
      <td class="num">${escHtml(fmtMoney(taxable))}</td>
      <td>0</td>
      <td class="num">${escHtml(fmtMoney(taxable))}</td>
      <td>${escHtml(cgstPct)}</td>
      <td class="num">${escHtml(fmtMoney(cgstAmt))}</td>
      <td>${escHtml(igstPct)}</td>
      <td class="num">${escHtml(fmtMoney(igstAmt))}</td>
      <td class="num"><b>${escHtml(fmtMoney(gross))}</b></td>
    </tr>`;
  }).join("");
  const grandTotal = Math.round(data.total * 100) / 100;
  const totalTaxable = Math.round(data.subtotal * 100) / 100;
  const totalCgst = Math.round(data.cgst_amt * 100) / 100;
  const totalIgst = Math.round(data.igst_amt * 100) / 100;

  return `<section class="receipt" data-panel="${bucket}">
    <button class="print-btn no-print" onclick="window.__printPanel('${bucket}')">Print ${escHtml(title.split(" ")[0])}</button>
    <h2 class="title">${escHtml(title)}</h2>
    <div class="meta">
      <div>Invoice No: <b>${data.has_content ? escHtml(invNo(inv.numbers, bucket, inv.provisional)) : "—"}</b></div>
      <div>Invoice Date: <b>${escHtml(fmtDate(sale.sale_date))}</b></div>
    </div>
    <hr/>
    <div class="party">
      <div class="party-col">
        <div class="party-name">${escHtml((settings || {}).name || "—")}</div>
        <div>${escHtml(((settings || {}).address || "").split("\n").join(", "))}</div>
        <div>State Name: ${escHtml((settings || {}).state_name || "—")} &nbsp; State Code: ${escHtml((settings || {}).state_code || "—")}</div>
        <div>GST No : <b>${escHtml((settings || {}).gstin || "—")}</b></div>
      </div>
      <div class="party-col">
        <div><span class="lbl">Customer</span> : <b>${escHtml(c.name || "—")}</b></div>
        <div><span class="lbl">Mobile</span> &nbsp;&nbsp;&nbsp;: ${escHtml(c.phone || "—")}</div>
        <div><span class="lbl">Address</span> : ${escHtml(c.address || "—")}</div>
        <div><span class="lbl">Cust GST No</span> : ${escHtml(c.gst_number || c.gstin || "—")}</div>
      </div>
    </div>
    ${data.has_content
      ? `<table class="charges">
          <thead><tr>
            <th>SN</th>
            <th>Particulars</th>
            <th>HSN</th>
            <th>Qty</th>
            <th>Unit Cost</th>
            <th>Disc</th>
            <th>Taxable</th>
            <th>CGST %</th>
            <th>Amount</th>
            <th>SGST %</th>
            <th>Amount</th>
            <th>Total</th>
          </tr></thead>
          <tbody>${itemRows}</tbody>
          <tfoot>
            <tr>
              <td colspan="6" class="num"><b>Sub-total</b></td>
              <td class="num"><b>${escHtml(fmtMoney(totalTaxable))}</b></td>
              <td></td>
              <td class="num"><b>${escHtml(fmtMoney(totalCgst))}</b></td>
              <td></td>
              <td class="num"><b>${escHtml(fmtMoney(totalIgst))}</b></td>
              <td class="num"><b>${escHtml(fmtMoney(grandTotal))}</b></td>
            </tr>
            <tr><td colspan="11" class="num"><b>Grand Total</b></td><td class="num"><b>${escHtml(fmtMoney(grandTotal))}</b></td></tr>
          </tfoot>
        </table>
        <div class="words">Amount In Words: <b>${escHtml(numToIndianWords(grandTotal))}</b></div>`
      : `<div class="empty">No ${escHtml(bucket)} items on this sale.</div>`}

    ${signaturesHtml(settings || {})}
  </section>`;
}

// Compose the combined side-by-side document.
function receiptBuckets(sale) {
  return {
    ex_showroom: buildReceiptBucket(sale.packages || [], "ex_showroom"),
    helmet: buildReceiptBucket(sale.packages || [], "helmet"),
    accessories: buildReceiptBucket(sale.packages || [], "accessories"),
  };
}

function combinedHtml(sale, settings, inv) {
  const { ex_showroom: exData, helmet: helmetData, accessories: accData } = receiptBuckets(sale);

  return `<!doctype html><html><head><title>Receipts · ${escHtml(sale.id.slice(0, 8).toUpperCase())}</title>
<style>
  *{box-sizing:border-box}
  body{font-family:'Times New Roman',serif;color:#111;margin:18px;background:#f6f6f9}
  .row{display:flex;gap:14px;align-items:flex-start}
  .receipt{flex:1 1 0;min-width:0;background:#fff;border:1px solid #ddd;padding:18px;font-size:11.5px;line-height:1.4;position:relative}
  .formal{flex:2 1 0}
  .simple{flex:1 1 0}
  .print-btn{position:absolute;top:8px;right:8px;background:#0A0A0A;color:#fff;border:0;padding:6px 10px;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;cursor:pointer}
  .print-btn:hover{background:#1F60FF}
  .hdr{display:flex;align-items:flex-start;justify-content:flex-start;gap:24px;margin-bottom:6px}
  .logo{max-height:72px;max-width:170px;object-fit:contain}
  .logo-fallback{font-family:'Cabinet Grotesk',sans-serif;font-weight:800;font-size:18px;color:#111}
  .hdr-title{flex:1;text-align:center;font-size:18px;font-weight:700;margin:18px 0 0 0;font-family:'Times New Roman',serif}
  .meta{display:flex;justify-content:space-between;font-size:11px;margin:4px 0 8px}
  .simple-meta{justify-content:flex-start;gap:8px;color:#555;font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.08em}
  hr{border:0;border-top:1px solid #000}
  .party{display:flex;gap:24px;padding:8px 0}
  .party-col{flex:1 1 50%}
  .party-name{font-weight:700;margin-bottom:2px}
  .party-col .lbl{display:inline-block;min-width:90px;color:#555}
  table{width:100%;border-collapse:collapse;margin:6px 0}
  .charges th,.charges td{border:none;padding:4px 6px;font-size:10.5px}
  .charges thead th{font-weight:700;border-bottom:1px solid #000;text-align:left;background:#fff}
  .charges tfoot td{padding:3px 6px}
  .charges .num,.charges-simple .num{text-align:right}
  .charges .subhdr{padding-top:8px;font-style:italic;color:#444;font-size:10px}
  .lineitem td{padding-top:5px;padding-bottom:5px}
  .words{margin:8px 0;padding:6px 0;border-top:1px solid #000;font-size:11.5px}
  .parts th,.parts td{border:none;padding:4px 6px;font-size:10.5px;text-align:left}
  .parts thead th{border-bottom:1px solid #000;font-weight:700}
  .parts .mono,.kv .mono{font-family:'Courier New',monospace}
  .hpa{margin-top:8px;font-size:11.5px}
  .exshow{margin-top:18px;font-weight:700;font-size:12px}
  .received{margin-top:8px;font-size:11.5px;line-height:1.7}
  .signatures{display:flex;gap:40px;margin-top:30px}
  .sig-col{flex:1}
  .sig-line{border-bottom:1px solid #000;height:32px;margin-bottom:4px}
  .sig-with-seal{position:relative;padding-top:80px}
  .seal{position:absolute;top:-8px;left:50%;width:124px;height:124px;transform:translateX(-50%) rotate(-8deg);opacity:.88}
  .simple-title{font-family:'Cabinet Grotesk','Helvetica',sans-serif;font-size:20px;font-weight:800;margin:6px 0 4px}
  .card{border:1px solid #0A0A0A;padding:10px;margin:8px 0}
  .card-title{font-family:'IBM Plex Mono',monospace;font-size:9px;text-transform:uppercase;letter-spacing:.2em;color:#666;margin-bottom:6px}
  .kv{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dashed #ddd;font-size:11px}
  .kv:last-child{border-bottom:0}
  .charges-simple th{font-family:'IBM Plex Mono',monospace;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:#666;border-bottom:1px solid #0A0A0A;text-align:left;padding:5px 6px}
  .charges-simple td{padding:5px 6px;border-bottom:1px solid #eee;font-size:11px}
  .charges-simple .subtotal td{background:#fafafa;border-top:1px solid #0A0A0A;border-bottom:1px solid #0A0A0A}
  .charges-simple tfoot td{font-size:12px;border-top:2px solid #0A0A0A;padding-top:6px}
  .empty{padding:30px;border:1px dashed #ccc;text-align:center;color:#888;font-family:'IBM Plex Mono',monospace;font-size:10px}
  .toolbar{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
  .toolbar .left{font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:.2em;color:#555}
  .toolbar .right button{background:#1F60FF;color:#fff;border:0;padding:8px 14px;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;margin-left:6px}
  .toolbar .right button:hover{background:#0A0A0A}

  @media print{
    body{background:#fff;margin:0}
    .toolbar,.no-print,.print-btn{display:none !important}
    .row{display:block}
    .receipt{border:none;padding:8px;page-break-after:always}
    .receipt:last-child{page-break-after:auto}
    /* Hide non-selected panels when printing one at a time. */
    body.print-only-ex_showroom .receipt:not([data-panel="ex_showroom"]){display:none}
    body.print-only-helmet .receipt:not([data-panel="helmet"]){display:none}
    body.print-only-accessories .receipt:not([data-panel="accessories"]){display:none}
  }
</style></head><body>
<div class="toolbar no-print">
  <div class="left">3-Receipt RTO Bundle · Sale ${escHtml(sale.id.slice(0, 8).toUpperCase())} · ${escHtml(fmtDate(sale.sale_date))}</div>
  <div class="right">
    <button onclick="window.__printPanel('all')">Print All</button>
  </div>
</div>
<div class="row">
  ${exShowroomHtml(sale, settings, exData, inv)}
  ${simpleHtml(sale, "helmet", "Helmet Invoice", helmetData, settings, inv)}
  ${simpleHtml(sale, "accessories", "Accessories Invoice", accData, settings, inv)}
</div>
<script>
  window.__printPanel = function(which){
    document.body.classList.remove("print-only-ex_showroom","print-only-helmet","print-only-accessories");
    if (which !== "all") document.body.classList.add("print-only-" + which);
    window.print();
    setTimeout(function(){
      document.body.classList.remove("print-only-ex_showroom","print-only-helmet","print-only-accessories");
    }, 200);
  };
</script>
</body></html>`;
}

// Cache settings for the session so we don't refetch on every click.
let _settingsCache = null;
async function loadSettings() {
  if (_settingsCache) return _settingsCache;
  try {
    const { data } = await api.get("/settings/dealership");
    _settingsCache = data || {};
  } catch {
    _settingsCache = {};
  }
  return _settingsCache;
}

export default function ReceiptPrintMenu({ sale }) {
  const openReceipts = async () => {
    const w = window.open("", "_blank", "width=1400,height=1000");
    if (!w) return;
    const settings = await loadSettings();
    const buckets = receiptBuckets(sale);
    const types = ["ex_showroom", "helmet", "accessories"].filter((t) => t === "ex_showroom" || buckets[t].has_content);
    let inv;
    try {
      const { data } = await api.post(`/sales/${sale.id}/invoice-numbers`, { types });
      inv = { numbers: data.invoice_numbers || {}, provisional: data.provisional };
    } catch {
      inv = { numbers: sale.invoice_numbers || {}, provisional: false };
    }
    w.document.write(combinedHtml(sale, settings, inv));
    w.document.close();
  };

  return (
    <button
      type="button"
      onClick={openReceipts}
      data-testid={`receipt-menu-${sale.id}`}
      title="Open 3-receipt bundle (ex-showroom + helmet + accessories)"
      className="p-1.5 border border-canvas-line hover:border-ink flex items-center gap-1"
    >
      <Printer size={12} weight="bold" />
    </button>
  );
}
